import { useEffect, useContext, useRef } from "react";
import { DataContext } from "../context/DataContext";
import { getEdgeStatusUrl } from "../utils/edge";
import { isReviewHoldResult, isTerminalResult } from "../utils/reviewHold";

const useEdgeStatusCheck = (triggerCondition, customHandler = null, isHandoff = false, onResult = null) => {
  const initialDelay = 500;
  const interval = 700;
  const { settings, dispatch } = useContext(DataContext);
  const isRequestPendingRef = useRef(false); // Track if a request is in progress

  useEffect(() => {
    if (!triggerCondition) return;

    const requestBody = {
      request_id: settings.request_id,
    };

    // Add handoff flag if specified
    if (isHandoff) {
      requestBody.handoff = true;
    }

    const handleStatusResponse = (result) => {
      if (!result) return true; // Continue polling if no result

      const {
        status,
        should_continue_polling: shouldContinuePolling,
        process_state: processState,
      } = result;

      // Record whether this terminal outcome is a review hold before
      // any handler maps it to FAIL. DataContext reads the flag to show
      // the "under review" screen instead of the failure screen.
      if (isTerminalResult(result)) {
        dispatch({
          type: "SET_REVIEW_HOLD",
          payload: isReviewHoldResult(result, settings?.journey?.settings),
        });
      }

      if (customHandler) {
        const handlerResponse = customHandler(result, false);
        // Custom handler decides: false = stop polling, true = keep polling
        return handlerResponse;
      } else {
        if (processState === "COMPLETED") {
          if (status === "PASS") {
            if (onResult) onResult(status);
            dispatch({ type: "SET_STATUS", payload: "PASS" });
            dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
            return false;
          }

          if (onResult) onResult(status);
          dispatch({ type: "SET_STATUS", payload: "FAIL" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
          return false;
        }

        if (status === "PASS") {
          if (onResult) onResult(status);
          dispatch({ type: "SET_STATUS", payload: "PASS" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
          return false;
        }

        if (status === "FAIL") {
          if (onResult) onResult(status);
          dispatch({ type: "SET_STATUS", payload: "FAIL" });
          dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
          return false;
        }
      }

      if (shouldContinuePolling) {
        return true;
      }

      if (!customHandler && processState === "NEXT_APP_TRIGGER") {
        // Current app is done, move to the next app in the journey
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: true });
        dispatch({ type: "TRIGGER_NAVIGATION" });
        return false;
      }

      if (!customHandler && processState === "WAITING") {
        dispatch({ type: "SET_STATUS", payload: "PENDING" });
        dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
        return false;
      }

      if (!customHandler && processState === "INITIALIZED") {
        // Keep polling when initialized
        return true;
      }

      if (!customHandler && processState === "HANDED_OFF") {
        // Keep polling when handed off to mobile
        return true;
      }

      return false;
    };

    const fetchStatusCheck = async () => {
      // Skip this request if another one is already in progress
      if (isRequestPendingRef.current) {
        return;
      }
      
      try {
        // Mark that we're starting a request
        isRequestPendingRef.current = true;
        
        const edgeStatusUrl = getEdgeStatusUrl();

        const response = await fetch(edgeStatusUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch status");
        }

        const result = await response.json();
        const shouldContinue = handleStatusResponse(result);
        
        if (!shouldContinue) {
          clearInterval(intervalId);
        }
      } catch (error) {
        // Continue silently without breaking the flow
        console.error("Edge status check error:", error);
      } finally {
        // Mark that the request has completed
        isRequestPendingRef.current = false;
      }
    };

    // Initial fetch with delay
    let timeoutId = setTimeout(() => {
      fetchStatusCheck();
      
      // Then set up the interval for subsequent fetches
      intervalId = setInterval(fetchStatusCheck, interval);
    }, initialDelay);
    
    let intervalId;

    return () => {
      clearTimeout(timeoutId);
      clearInterval(intervalId);
      // Reset pending flag when unmounting
      isRequestPendingRef.current = false;
    };
  }, [
    triggerCondition,
    settings.request_id,
    dispatch,
    customHandler,
  ]);
};

export default useEdgeStatusCheck;
